// src/pages/Gestione.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./gestione.css";
import { Sunrise, Sun, MoonStar } from "lucide-react";

/**
 * Pagina "Gestione" (Tab: Da gestire)
 *
 * - Topbar: Zona → Rilevatore (della zona) → contatore "App. da gestire" (solo PASSA_AL_SENIOR / NON_BUONO)
 * - Lista a sinistra: righe per data + card appuntamento.
 * - Modale "Gestione appuntamento": dati cliente, budget, planner 10×3 e log + azioni.
 */

const API_AGENDA = "/preventivi/agenda/api";
const API_GESTIONE = "/preventivi/gestione/api";
const API_USERS = "/preventivi/api";



// utente loggato (stessa logica di AgendaPro)
// utente loggato (stessa logica di AgendaPro)
const UID =
  (window.__APP__ &&
    (window.__APP__.UID ||
      window.__APP__.uid ||
      window.__APP__.user_id)) ||
  window.UID ||
  window.uid ||
  window.USER_ID ||
  null;






const PERIOD_DAYS_BACK = 4;
const PLANNER_DAYS = 10; // numero di giorni mostrati nel planner

const VISIBLE = { PASSA: "PASSA_AL_SENIOR", NON_BUONO: "NON_BUONO" };

const ZONE = [
  { id: 1, label: "Verona" },
  { id: 2, label: "Modena" },
  { id: 3, label: "Novara" },
  { id: 4, label: "Milano Sud" },
  { id: 5, label: "Milano Nord" },
];

// ---------- utilità di base ----------

const pad2 = (n) => String(n).padStart(2, "0");

const fmtDateTime = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;

const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function dayParts(date) {
  const dow = new Intl.DateTimeFormat("it-IT", { weekday: "long" }).format(
    date
  );
  const mon = new Intl.DateTimeFormat("it-IT", { month: "long" }).format(date);
  return { n: date.getDate(), dow: capFirst(dow), mon: capFirst(mon) };
}

async function fetchJSON(url) {
  const res = await fetch(url, {
    credentials: "include",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  const ct = res.headers.get("content-type") || "";
  const txt = await res.text();
  let j = null;
  if (ct.includes("application/json")) {
    try {
      j = txt ? JSON.parse(txt) : null;
    } catch {
      j = null;
    }
  }
  if (!res.ok) {
    throw new Error(j?.message || `HTTP ${res.status} ${res.statusText}`);
  }
  return j ?? txt;
}

/** Normalizza record evento proveniente da list_events.php */
function normalizeEvent(e) {
  const id =
    e.id ??
    e.event_id ??
    e.agenda_id ??
    e.uid ??
    e.pk ??
    `${e.start}-${e.nome}`;
  const start =
    e.start_dt || e.start || e.data || e.inizio || e.datetime || e.start_time;

  const wantSenior =
    Number(e.want_senior || e.senior_want || e.pass_to_senior || 0) === 1;
  const rilKo = Number(e.ril_non_buono || 0) === 1;

  const status = wantSenior
    ? VISIBLE.PASSA
    : rilKo
    ? VISIBLE.NON_BUONO
    : e.status || e.ui_state_code || "";

  // Budget / Real (coerente con AgendaPro)
  const pv_budget_ril =
    e.pv_budget_rilevatore_eur ?? e.preventivo_budget_rilevatore_eur;
  const pv_budget_sen =
    e.pv_budget_senior_eur ?? e.preventivo_budget_senior_eur;
  const pv_budget_std = e.pv_budget ?? e.preventivo_budget_eur;

  const budget =
    pv_budget_std != null
      ? Number(pv_budget_std)
      : pv_budget_ril != null
      ? Number(pv_budget_ril)
      : pv_budget_sen != null
      ? Number(pv_budget_sen)
      : null;

  const real =
    e.senior_importo_eur != null &&
    String(e.senior_esito || "").toLowerCase() === "chiuso"
      ? Number(e.senior_importo_eur)
      : e.pv_total != null
      ? Number(e.pv_total)
      : e.preventivo_target_eur != null
      ? Number(e.preventivo_target_eur)
      : null;

  const rilId =
    e.rilevatore_id ?? e.user_id ?? e.assigned_rilevatore_id ?? null;
  const zonaId = e.zona_id ?? e.zone_id ?? null;

  const seniorId =
    e.senior_id ??
    e.senior ??
    e.sid ??
    null;



  return {
    id,
    data: start,
    status,
    want_senior: wantSenior ? 1 : 0,
    ril_non_buono: rilKo ? 1 : 0,

    // anagrafica
    nome:
      e.nome_cliente ||
      e.cliente_nome ||
      e.nome ||
      e.customer_name ||
      "",
    telefono:
      e.cliente_telefono ||
      e.cliente_tel ||
      e.telefono ||
      e.phone ||
      "",

    zona_id: zonaId,
    rilevatore_id: rilId,
    senior_id: seniorId,   // 👈 NUOVO
    budget,
    real,
  };
}

/** % scostamento + “vicinanza” al Real (0..1) */
function computeDiff(budget, real) {
  const R = Number(real || 0);
  const B = Number(budget || 0);
  if (!Number.isFinite(R) || R <= 0 || !Number.isFinite(B)) return null;
  const diffAbs = Math.abs(B - R) / R;
  const proximity = Math.max(0, Math.min(1, 1 - diffAbs));
  const score = Math.round(diffAbs * 100);
  const sign = B >= R ? "+" : "−";
  return { score, sign, proximity };
}

/** Costruisce il path per AgendaPro: "/agenda-pro?day=YYYY-MM-DD&event=ID" */
function buildAgendaUrl(app) {
  if (!app?.id || !app?.data) return "";

  const d = new Date(app.data);
  if (Number.isNaN(d.getTime())) return "";

  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const ymd = `${y}-${m}-${dd}`;

  const qs = new URLSearchParams({
    day: ymd,
    event: String(app.id),
  }).toString();

  return `/agenda-pro?${qs}`;
}

// ---------- helper flags icone da log ----------

/**
 * Ogni flag: telefono = lista (max 3), gli altri = ultimo evento (lastAt + operatorId)
 */
function computeEventFlagsFromLog(azioni) {
  const flags = {
    telNoAnswer: [], // array di { lastAt, operatorId }
    telCallback: [], // array di { lastAt, operatorId }
    whatsapp: null, // singolo { lastAt, operatorId }
    noInterestSite: null,
    noInterestMoney: null,
    seniorFix: null,
  };

  (azioni || []).forEach((r) => {
    const info = {
      lastAt: r.at || null,
      operatorId:
        typeof r.operator_id === "number"
          ? r.operator_id
          : r.operator_id
          ? Number(r.operator_id)
          : null,
    };

    switch (r.action) {
      case "fmt_tel_no_answer":
        flags.telNoAnswer.push(info);
        break;

      case "fmt_tel_callback":
        flags.telCallback.push(info);
        break;

      case "fmt_whatsapp":
        flags.whatsapp = info;
        break;

      case "fmt_no_interest_site":
        flags.noInterestSite = info;
        break;

      case "fmt_no_interest_money":
        flags.noInterestMoney = info;
        break;

      case "fmt_senior_fix":
        flags.seniorFix = info;
        break;

      default:
        break;
    }
  });

  // Tieni solo gli ULTIMI 3 tentativi telefono (se ce ne sono di più)
  if (flags.telNoAnswer.length > 3) {
    flags.telNoAnswer = flags.telNoAnswer.slice(-3);
  }
  if (flags.telCallback.length > 3) {
    flags.telCallback = flags.telCallback.slice(-3);
  }

  return flags;
}











// ---------- Component --------------------------------------------------------

export default function Gestione() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [events, setEvents] = useState([]);
  const [utenti, setUtenti] = useState([]);

  const [zonaSel, setZonaSel] = useState("ALL");
  const [rilSel, setRilSel] = useState("ALL");

  // stato modale
  const [gestioneOpen, setGestioneOpen] = useState(false);
  const [gestioneEventId, setGestioneEventId] = useState(null);
  const [gestioneData, setGestioneData] = useState(null);
  const [gestioneLoading, setGestioneLoading] = useState(false);
  const [gestioneErr, setGestioneErr] = useState("");

  // selezione planner
  const [selectedDay, setSelectedDay] = useState(null); // "YYYY-MM-DD"
  const [selectedFascia, setSelectedFascia] = useState(null); // "M" | "P" | "S"
  const [selectedSeniorId, setSelectedSeniorId] = useState(null); // id senior
  const [weekOffset, setWeekOffset] = useState(0); // 0 = dal giorno app, >0 pagine successive di 10 giorni
  const [agendaZonaId, setAgendaZonaId] = useState(null); // zona della micro-agenda/planner
  const [mapNonce, setMapNonce] = useState(0); // <--- QUI


    const [assignOpen, setAssignOpen] = useState(false); // visibilità micromodale
  const [assignSlot, setAssignSlot] = useState({ day: null, fascia: null });
  const [assignTime, setAssignTime] = useState("");    // HH:MM
  const [assignNote, setAssignNote] = useState("");    // nota breve



  // flags icone per ogni evento (event_id -> flags)
  const [eventFlags, setEventFlags] = useState({});

  // fasce da "spegnere" (M/P/S)
  const [hiddenFasce, setHiddenFasce] = useState({
    M: false,
    P: false,
    S: false,
  });

  // filtri per dimmare slot/giorni
  const [slotFilters, setSlotFilters] = useState({
    dim2plus: false, // dimma slot con ≥2 app
    dim4day: false,  // dimma giornate con somma app ≥4
  });



    const navigate = useNavigate();

  // chiusura modali con ESC
  useEffect(() => {
    if (!gestioneOpen && !assignOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape" || e.key === "Esc" || e.keyCode === 27) {
        if (assignOpen) {
          setAssignOpen(false);
          return;
        }
        if (gestioneOpen) {
          setGestioneOpen(false);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gestioneOpen, assignOpen]);

  // Periodo: ultimi 4 giorni per la lista
  const range = useMemo(() => {
    const end = new Date();
    const start = new Date();
    end.setHours(23, 59, 59, 999);
    start.setDate(start.getDate() - PERIOD_DAYS_BACK);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }, []);

  // Carica flags dal log per una lista di eventi
  const loadFlagsForEvents = useCallback(async (evts) => {
    const ids = (evts || [])
      .map((e) => Number(e.id))
      .filter((id) => id > 0);

    if (!ids.length) return;

    try {
      const res = await fetch("/preventivi/gestione/api/gestione_flags.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ event_ids: ids }),
      });

      const data = await res.json();

      if (!res.ok || !data || data.status !== "OK") {
        throw new Error(data?.message || "Errore caricamento flags gestione");
      }

      if (data.logs && typeof data.logs === "object") {
        const nextFlags = {};
        Object.entries(data.logs).forEach(([eventId, azioni]) => {
          if (Array.isArray(azioni)) {
            nextFlags[eventId] = computeEventFlagsFromLog(azioni);
          }
        });

        setEventFlags((prev) => ({
          ...prev,
          ...nextFlags,
        }));
      }
    } catch (e) {
      console.error("loadFlagsForEvents error:", e);
      // non blocchiamo la UI se fallisce: semplicemente niente iconcine
    }
  }, []);

  // Carica appuntamenti + flags
  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const q = new URLSearchParams();
      q.set("start", fmtDateTime(range.start));
      q.set("end", fmtDateTime(range.end));
      if (zonaSel !== "ALL") {
        q.set("zona_id", String(zonaSel));
        q.set("zone_id", String(zonaSel));
      }
      const url = `${API_AGENDA}/list_events.php?${q.toString()}`;
      const data = await fetchJSON(url);
      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.events)
        ? data.events
        : [];

      const normEvents = arr.map(normalizeEvent);
      setEvents(normEvents);

      // flags dal log per questi eventi
      loadFlagsForEvents(normEvents);
    } catch (e) {
      setErr(e?.message || "Errore caricamento appuntamenti");
    } finally {
      setTimeout(() => setLoading(false), 50);
    }
  }, [range.start, range.end, zonaSel, loadFlagsForEvents]);

  useEffect(() => {
    load();
  }, [load]);

  // utenti (rilevatori/senior) per la zona selezionata
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const zonaParam =
          zonaSel === "ALL" ? "" : `?zona_id=${encodeURIComponent(zonaSel)}`;
        const url = `${API_USERS}/users_list.php${zonaParam}`;
        const data = await fetchJSON(url);
        const arr =
          Array.isArray(data?.rows) ||
          Array.isArray(data?.users) ||
          Array.isArray(data?.utenti)
            ? data.rows || data.users || data.utenti
            : Array.isArray(data)
            ? data
            : [];
        if (alive) setUtenti(arr);
      } catch {
        if (alive) setUtenti([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [zonaSel]);

  // Mantieni selectedDay sempre dentro la finestra corrente del planner 10×3
  useEffect(() => {
    if (!gestioneOpen || !gestioneData) return;

    const ev = gestioneData.event || {};
    const micro = gestioneData.microAgenda || {};

    const startStr =
      micro.day ||
      ev.start_dt ||
      ev.start ||
      ev.data ||
      ev.datetime ||
      null;

    if (!startStr) return;

    const baseDayStr = startStr.substring(0, 10);
    const baseDate = new Date(baseDayStr);
    if (Number.isNaN(baseDate.getTime())) return;

    const windowStart = new Date(baseDate);
    windowStart.setHours(0, 0, 0, 0);
    windowStart.setDate(windowStart.getDate() + weekOffset * PLANNER_DAYS);

    const windowEnd = new Date(windowStart);
    windowEnd.setDate(windowStart.getDate() + PLANNER_DAYS - 1);

    // Se non c'è un giorno selezionato o è fuori dalla finestra, forzo il primo giorno visibile
    if (!selectedDay) {
      const y = windowStart.getFullYear();
      const m = pad2(windowStart.getMonth() + 1);
      const dd = pad2(windowStart.getDate());
      setSelectedDay(`${y}-${m}-${dd}`);
      return;
    }

    const cur = new Date(selectedDay);
    cur.setHours(0, 0, 0, 0);

    if (cur < windowStart || cur > windowEnd) {
      const y = windowStart.getFullYear();
      const m = pad2(windowStart.getMonth() + 1);
      const dd = pad2(windowStart.getDate());
      setSelectedDay(`${y}-${m}-${dd}`);
    }
  }, [gestioneOpen, gestioneData, weekOffset, selectedDay]);

  const normRole = (u) =>
    String(u.ruolo ?? u.role ?? "").trim().toLowerCase();

  const isActiveUser = (u) => {
    const v = u.attivo ?? u.active ?? 1;
    if (typeof v === "number") return v > 0;
    const s = String(v).toLowerCase();
    return ["1", "si", "sì", "true", "on", "ok"].includes(s);
  };

  const labelOf = (u) =>
    (u.nome && u.cognome && `${u.nome} ${u.cognome}`) ||
    u.nome ||
    u.name ||
    u.fullname ||
    u.display_name ||
    u.label ||
    u.username ||
    `#${u.id}`;

  const idOf = (u) => Number(u.id ?? u.user_id ?? 0);



const zoneOf = (u) => {
  const z =
    u.zona ??
    u.zone ??
    u.default_zone_id ??
    u.default_zona_id ??
    u.zona_id ??
    null;
  return z != null ? Number(z) : null;
};






  const rilOptions = useMemo(() => {
    const IS_RIL = new Set([
      "rilevatore",
      "rilievo",
      "ril",
      "junior",
      "tecnico",
      "field",
      "rilevatori",
    ]);
    return (utenti || [])
      .filter((u) => isActiveUser(u) && IS_RIL.has(normRole(u)))
      .map((u) => ({ id: idOf(u), label: labelOf(u) }))
      .filter((u) => u.id > 0 && u.label)
      .sort((a, b) => a.label.localeCompare(b.label, "it"));
  }, [utenti]);





    // elenco senior per la select della micromodale
    // elenco senior per la select della micromodale
const seniorOptions = useMemo(() => {
  const IS_SENIOR = new Set(["senior", "responsabile"]); // ruoli ammessi
  const zonaRef =
    agendaZonaId != null
      ? Number(agendaZonaId)     // zona dell’appuntamento
      : zonaSel !== "ALL"
      ? Number(zonaSel)          // se sto filtrando la pagina per zona
      : null;                    // altrimenti niente filtro

  return (utenti || [])
    .filter((u) => isActiveUser(u) && IS_SENIOR.has(normRole(u)))
    .filter((u) => {
      if (!zonaRef) return true;
      const z = zoneOf(u);       // legge u.zona / u.default_zone_id / u.zona_id
      return !z || z === zonaRef;
    })
    .map((u) => ({ id: idOf(u), label: labelOf(u) }))
    .filter((u) => u.id > 0 && u.label)
    .sort((a, b) => a.label.localeCompare(b.label, "it"));
}, [utenti, agendaZonaId, zonaSel]);






  // quando apro la micromodale, verifico che il senior selezionato sia valido
  useEffect(() => {
    if (!assignOpen) return;

    // se il senior corrente non è più nella lista (es. cambio zona) lo resetto
    if (
      selectedSeniorId &&
      !seniorOptions.some((s) => s.id === Number(selectedSeniorId))
    ) {
      setSelectedSeniorId(null);
    }

    // se non c'è selezione e c'è un solo senior disponibile → lo imposto di default
    if (!selectedSeniorId && seniorOptions.length === 1) {
      setSelectedSeniorId(seniorOptions[0].id);
    }
  }, [assignOpen, seniorOptions, selectedSeniorId]);








    // genera orari a mezz'ora per fascia (M/P/S)
  const buildTimeOptionsForFascia = (fasciaKey) => {
    let start = 8;
    let end = 12;

    if (fasciaKey === "P") {
      start = 12;
      end = 18;
    } else if (fasciaKey === "S") {
      start = 18;
      end = 21;
    }

    const out = [];
    for (let h = start; h < end; h++) {
      out.push(`${pad2(h)}:00`);
      out.push(`${pad2(h)}:30`);
    }
    return out;
  };

  // salto rapido alla "settimana prossima":
  // prende il prossimo lunedì rispetto a OGGI
  // e porta la finestra dei 10 giorni su quel lunedì.
  const jumpToNextWeekFromToday = useCallback(() => {
    if (!gestioneData) return;

    const ev = gestioneData.event || {};
    const micro = gestioneData.microAgenda || {};

    const startStr =
      micro.day ||
      ev.start_dt ||
      ev.start ||
      ev.data ||
      ev.datetime ||
      null;

    if (!startStr) return;

    // giorno base (quello dell'appuntamento) → ancora riferimento per weekOffset
    const baseDayStr = startStr.substring(0, 10); // "YYYY-MM-DD"
    const baseDate = new Date(baseDayStr);
    if (Number.isNaN(baseDate.getTime())) return;
    baseDate.setHours(0, 0, 0, 0);

    // oggi
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // prossimo lunedì (se oggi è lunedì → lunedì prossima settimana)
    const target = new Date(today);
    const dow = target.getDay(); // 0=dom, 1=lun, ... 6=sab
    let daysToNextMonday = (1 - dow + 7) % 7;
    if (daysToNextMonday === 0) {
      daysToNextMonday = 7;
    }
    target.setDate(target.getDate() + daysToNextMonday);

    // offset di "pagine" da 10 giorni
    const diffMs = target.getTime() - baseDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const offset = Math.floor(diffDays / PLANNER_DAYS);

    setWeekOffset(offset);

    const y = target.getFullYear();
    const m = pad2(target.getMonth() + 1);
    const dd = pad2(target.getDate());
    const ymd = `${y}-${m}-${dd}`;

    setSelectedDay(ymd);
    // di default fascia Pomeriggio (ma puoi cambiare in "M" o "S")
    if (!selectedFascia) {
      setSelectedFascia("P");
    }
  }, [gestioneData, selectedFascia]);



  // toggle per nascondere/mostrare una fascia (M/P/S)
  const toggleFasciaVisibility = useCallback((key) => {
    setHiddenFasce((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  // toggle per i filtri sugli slot
  const toggleSlotFilter = useCallback((key) => {
    setSlotFilters((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);







const zoneOptions = useMemo(() => {
  const usedIds = new Set(
    events
      .map((e) => Number(e.zona_id))
      .filter((id) => Number.isFinite(id) && id > 0)
  );
  return ZONE.filter(
    (z) => usedIds.size === 0 || usedIds.has(Number(z.id))
  );
}, [events]);


  // helper: nome utente/senior da id
  const nameFromId = useCallback(
    (id) => {
      if (!id) return "";

      // Fallback simpatico per id 2
      if (Number(id) === 2) return "The Powa";

      const u = (utenti || []).find((u) => idOf(u) === Number(id));
      return u ? labelOf(u) : `#${id}`;
    },
    [utenti]
  );

  // eventi visibili in lista
const rows = useMemo(() => {
  return events
    .filter((e) => {
      // includo solo:
      // - Passa al senior
      // - Non buono
      // ma escludo quelli che hanno già un senior assegnato
      const isVisibleStatus =
        e.status === VISIBLE.PASSA || e.status === VISIBLE.NON_BUONO;
      const hasSenior = Number(e.senior_id || 0) > 0;
      return isVisibleStatus && !hasSenior;
    })
    .filter((e) => {
      const d = new Date(e.data);
      return d >= range.start && d <= range.end;
    })
    .filter((e) =>
      zonaSel === "ALL" ? true : String(e.zona_id) === String(zonaSel)
    )
    .filter((e) =>
      rilSel === "ALL" ? true : String(e.rilevatore_id) === String(rilSel)
    )
    .sort((a, b) => new Date(b.data) - new Date(a.data));
}, [events, range, zonaSel, rilSel]);


 const passaCount    = rows.filter((r) => r.status === VISIBLE.PASSA).length;
const nonBuoniCount = rows.filter((r) => r.status === VISIBLE.NON_BUONO).length;


  // --- apertura modale gestione ---
  const handleOpenGestioneModal = async (app) => {
    setMapNonce((n) => n + 1);
    setGestioneOpen(true);
    setGestioneEventId(app.id);
    setGestioneLoading(true);
    setGestioneErr("");
    setGestioneData(null);

    try {
      const q = new URLSearchParams({ event_id: String(app.id) }).toString();
      const url = `${API_GESTIONE}/gestione_get.php?${q}`;
      const data = await fetchJSON(url);
      if (data.status && data.status !== "OK") {
        throw new Error(data.message || "Errore risposta server");
      }

      const ev = data.event || {};
      const start = ev.start_dt || ev.start || ev.data || ev.datetime || null;

      // zona di default per il planner = zona dell'appuntamento
      if (ev.zona_id) {
        setAgendaZonaId(Number(ev.zona_id));
      } else if (data.microAgenda && data.microAgenda.zona_id) {
        setAgendaZonaId(Number(data.microAgenda.zona_id));
      } else {
        setAgendaZonaId(null);
      }

      // reset pagina planner
      setWeekOffset(0);

      // default giorno/fascia = giorno & fascia dell'appuntamento
      if (start && start.length >= 16) {
        const dayStr = start.substring(0, 10);
        const timeStr = start.substring(11, 19);
        let fascia = "M";
        if (timeStr >= "11:30:00" && timeStr < "16:00:00") fascia = "P";
        else if (timeStr >= "16:00:00") fascia = "S";
        setSelectedDay(dayStr);
        setSelectedFascia(fascia);
      } else {
        setSelectedDay(null);
        setSelectedFascia(null);
      }

      // default senior
      let seniorId = ev.senior_id || null;
      if (
        !seniorId &&
        data.microAgenda &&
        Array.isArray(data.microAgenda.fasce)
      ) {
        const setIds = new Set();
        data.microAgenda.fasce.forEach((f) => {
          (f.slots || []).forEach((s) => {
            if (s.senior_id) setIds.add(Number(s.senior_id));
          });
        });
        const first = Array.from(setIds)[0];
        if (first) seniorId = first;
      }
      setSelectedSeniorId(seniorId || null);

      setGestioneData(data);

      // flags icone per questo evento
      if (Array.isArray(data.azioni)) {
        setEventFlags((prev) => ({
          ...prev,
          [app.id]: computeEventFlagsFromLog(data.azioni),
        }));
      }
    } catch (e) {
      setGestioneErr(e?.message || "Errore caricamento dati gestione");
    } finally {
      setGestioneLoading(false);
    }
  };





  // Apertura AgendaPro nella settimana corretta
  const handleOpenAgenda = useCallback(
    (app) => {
      const path = buildAgendaUrl(app);
      if (!path) {
        alert("Non riesco a calcolare la data dell’appuntamento.");
        return;
      }
      navigate(path);
    },
    [navigate]
  );

  // click destro



  
  // apertura micromodale per uno slot (riusata da click destro e ALT+click)
  const openAssignModalForSlot = useCallback(
    (dayYmd, fasciaKey) => {
      // seleziono lo slot nel planner
      setSelectedDay(dayYmd);
      setSelectedFascia(fasciaKey);

      // stato interno della micromodale
      setAssignSlot({ day: dayYmd, fascia: fasciaKey });

      // default orario: ora originale se è lo stesso giorno, altrimenti primo slot della fascia
      let defaultTime = "";
      const ev = gestioneData?.event;
      if (ev?.start_dt && String(ev.start_dt).startsWith(dayYmd)) {
        defaultTime = String(ev.start_dt).substring(11, 16); // HH:MM
      } else {
        const opts = buildTimeOptionsForFascia(fasciaKey);
        defaultTime = opts[0] || "09:00";
      }
      setAssignTime(defaultTime);

      // default senior: quello dell’appuntamento se c’è
           // default senior:
      // 1. se l'appuntamento ha già un senior e appartiene alla zona, uso quello
      // 2. altrimenti, se c'è un solo senior disponibile per la zona, uso lui
      // 3. altrimenti lascio la select vuota
      const currentSeniorId = gestioneData?.event?.senior_id
        ? Number(gestioneData.event.senior_id)
        : null;

      if (
        currentSeniorId &&
        seniorOptions.some((s) => s.id === currentSeniorId)
      ) {
        setSelectedSeniorId(currentSeniorId);
      } else if (!selectedSeniorId && seniorOptions.length === 1) {
        setSelectedSeniorId(seniorOptions[0].id);
      } else {
        setSelectedSeniorId(null);
      }





      // reset note e apri micromodale
      setAssignNote("");
      setAssignOpen(true);
    },
    [gestioneData, buildTimeOptionsForFascia, seniorOptions, selectedSeniorId]
  );

  // click destro nel planner
  const handlePlannerContextMenu = useCallback(
    (evt, dayYmd, fasciaKey) => {
      evt.preventDefault(); // niente menu del browser
      openAssignModalForSlot(dayYmd, fasciaKey);
    },
    [openAssignModalForSlot]
  );







  // Invio azioni al log
  const sendGestioneAction = useCallback(
  async (action, extraPayload = {}) => {
    if (!gestioneEventId) {
      console.error("sendGestioneAction: gestioneEventId mancante", {
        action,
        extraPayload,
        selectedDay,
        selectedFascia,
        selectedSeniorId,
      });
      alert("Errore interno: ID appuntamento mancante. Chiudi e riapri la scheda di gestione.");
      return;
    }

    if (!action) {
      console.error("sendGestioneAction: action mancante");
      return;
    }

    const opId = Number(UID || 0);   // 👈 qui calcoli l'operatore loggato

const payload = {
  event_id: Number(gestioneEventId),
  action,
  day: selectedDay || null,
  fascia: selectedFascia || null,
  senior_id: selectedSeniorId ? Number(selectedSeniorId) : null,
  operator_id: opId,          // <--- AGGIUNGI QUESTA
  payload: extraPayload || {},
};


    try {
      const res = await fetch(
        "/preventivi/gestione/api/gestione_action.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (
        !res.ok ||
        !data ||
        (data.status !== "OK" && data.status !== "RATE_LIMIT")
      ) {
        throw new Error(data?.message || "Errore salvataggio azione");
      }

      if (data.status === "RATE_LIMIT") {
        alert(data.message || "Azione già registrata di recente.");
        return;
      }

      // Aggiorna log + flags
      if (Array.isArray(data.azioni)) {
        setGestioneData((prev) =>
          prev ? { ...prev, azioni: data.azioni } : prev
        );

        setEventFlags((prev) => ({
          ...prev,
          [gestioneEventId]: computeEventFlagsFromLog(data.azioni),
        }));
      }

      // Se ho fissato il senior, ricarico la lista
      if (action === "fmt_senior_fix") {
        load();
      }
    } catch (e) {
      console.error(e);
      alert(e.message || "Errore nel salvataggio dell’azione.");
    }
  },
  [gestioneEventId, selectedDay, selectedFascia, selectedSeniorId, load]
);






  // ===== RENDER ======================================================
  return (
    <div className="gestione-page">
      <div className="gestione-container">
        {/* Topbar */}
<header className="topbar">
  <select
    className="select"
    value={zonaSel}
    onChange={(e) => {
      setZonaSel(e.target.value);
      setRilSel("ALL");
    }}
  >
    <option value="ALL">Tutte le zone</option>
    {zoneOptions.map((z) => (
      <option key={z.id} value={String(z.id)}>
        {z.label}
      </option>
    ))}
  </select>

  <select
    className="select"
    value={rilSel}
    onChange={(e) => setRilSel(e.target.value)}
  >
    <option value="ALL">Tutti i rilevatori</option>
    {rilOptions.map((r) => (
      <option key={r.id} value={String(r.id)}>
        {r.label}
      </option>
    ))}
  </select>

  <div className="counter">
    App. da gestire (<b>Passa al senior</b>):{" "}
    <strong>{passaCount}</strong>
    {" · "}
    <span style={{ opacity: 0.8 }}>
      Non buoni: <strong>{nonBuoniCount}</strong>
    </span>
  </div>
</header>


        {/* Lista righe */}
        <section className="rows">
          {loading && <div className="muted">Carico gli appuntamenti…</div>}
          {err && !loading && (
            <div className="muted" style={{ color: "#fca5a5" }}>
              Errore: {err}
            </div>
          )}
          {!loading && !err && rows.length === 0 && (
            <div className="muted">
              Nessun appuntamento "Passa al senior / Non buono" nel periodo.
            </div>
          )}

          {rows.map((app) => {
            const d = new Date(app.data);
            const { n, dow, mon } = dayParts(d);
            const diff = computeDiff(app.budget, app.real);
            const statusClass =
              app.status === VISIBLE.PASSA
                ? "appt appt--passa-senior"
                : "appt appt--non-buono";

            const flags = eventFlags[app.id] || {};

            return (
              <div className="row" key={app.id}>
                {/* Colonna data */}
                <div className="row__day">
                  <div className="day">
                    <div className="day__num">{n}</div>
                    <div className="day__meta">
                      <div className="dow">{dow}</div>
                      <div className="mon">{mon}</div>
                    </div>
                  </div>
                </div>

                {/* Card appuntamento */}
                <div className="row__card">
                  <article className={statusClass}>
                    <div className="appt__info">
                      <div className="appt__name">
                        {app.nome || "—"}
                        <span
                          className="score-inline"
                          style={{
                            "--proximity":
                              diff && typeof diff.proximity === "number"
                                ? diff.proximity
                                : 0,
                          }}
                          title={
                            diff && app.budget != null && app.real != null
                              ? `Budget: ${app.budget} • Real: ${app.real}`
                              : "Budget/Real non disponibili"
                          }
                        >
                          {diff ? `${diff.sign}${diff.score}%` : "—"}
                        </span>
                      </div>

                      {/* riga telefono + iconcine */}
                      <div className="appt__meta-row">
                        <span className="appt__phone">
                          {app.telefono || ""}
                        </span>

                        {/* icone stato contatto */}
                        <div className="appt__icons">
                          {/* TEL NO ANSWER (max 3) */}
                          {(flags.telNoAnswer || []).map((item, idx) => {
                            const opName = item.operatorId
                              ? nameFromId(item.operatorId)
                              : "Operatore sconosciuto";
                            return (
                              <span
                                key={`${app.id}-noans-${idx}`}
                                className="appt-ic-wrapper appt-ic--tel-red"
                                title={
                                  "Telefonata senza risposta" +
                                  (item.lastAt ? " • " + item.lastAt : "") +
                                  " • " + opName
                                }
                              >
                                <span className="appt-ic">📞</span>
                              </span>
                            );
                          })}

                          {/* TEL CALLBACK (max 3) */}
                          {(flags.telCallback || []).map((item, idx) => {
                            const opName = item.operatorId
                              ? nameFromId(item.operatorId)
                              : "Operatore sconosciuto";
                            return (
                              <span
                                key={`${app.id}-cb-${idx}`}
                                className="appt-ic-wrapper appt-ic--tel-yellow"
                                title={
                                  "Da richiamare" +
                                  (item.lastAt ? " • " + item.lastAt : "") +
                                  " • " + opName
                                }
                              >
                                <span className="appt-ic">⏰</span>
                              </span>
                            );
                          })}

                          {/* WHATSAPP */}
                          {flags.whatsapp &&
                            (() => {
                              const item = flags.whatsapp;
                              const opName = item.operatorId
                                ? nameFromId(item.operatorId)
                                : "Operatore sconosciuto";
                              return (
                                <span
                                  className="appt-ic-wrapper appt-ic--wa"
                                  title={
                                    "WhatsApp inviato" +
                                    (item.lastAt ? " • " + item.lastAt : "") +
                                    " • " + opName
                                  }
                                >
                                  <span className="gm-wa-icon">💬</span>
                                </span>
                              );
                            })()}

                          {/* NON INTERESSATO CANTIERE */}
                          {flags.noInterestSite &&
                            (() => {
                              const item = flags.noInterestSite;
                              const opName = item.operatorId
                                ? nameFromId(item.operatorId)
                                : "Operatore sconosciuto";
                              return (
                                <span
                                  className="appt-ic-wrapper appt-ic--cant"
                                  title={
                                    "Non interessato (cantiere)" +
                                    (item.lastAt ? " • " + item.lastAt : "") +
                                    " • " + opName
                                  }
                                >
                                  <span className="appt-ic">🚧</span>
                                </span>
                              );
                            })()}

                          {/* NON INTERESSATO SOLDI */}
                          {flags.noInterestMoney &&
                            (() => {
                              const item = flags.noInterestMoney;
                              const opName = item.operatorId
                                ? nameFromId(item.operatorId)
                                : "Operatore sconosciuto";
                              return (
                                <span
                                  className="appt-ic-wrapper appt-ic--money"
                                  title={
                                    "Non interessato (budget)" +
                                    (item.lastAt ? " • " + item.lastAt : "") +
                                    " • " + opName
                                  }
                                >
                                  <span className="appt-ic">💸</span>
                                </span>
                              );
                            })()}

                          {/* SENIOR FIX */}
                          {flags.seniorFix &&
                            (() => {
                              const item = flags.seniorFix;
                              const opName = item.operatorId
                                ? nameFromId(item.operatorId)
                                : "Operatore sconosciuto";
                              return (
                                <span
                                  className="appt-ic-wrapper appt-ic--ok"
                                  title={
                                    "Appuntamento fissato col senior" +
                                    (item.lastAt ? " • " + item.lastAt : "") +
                                    " • " + opName
                                  }
                                >
                                  <span className="appt-ic">✅</span>
                                </span>
                              );
                            })()}
                        </div>
                      </div>
                    </div>

                    <div className="appt__actions">
                      <button
                        className="btn btn--icon"
                        type="button"
                        onClick={() => handleOpenGestioneModal(app)}
                        aria-label="Gestione appuntamento"
                        title="Gestione appuntamento"
                      >
                        ⚙️
                      </button>

                      <button
                        className="btn btn--primary btn--icon"
                        type="button"
                        onClick={() => handleOpenAgenda(app)}
                        aria-label="Apri in Agenda"
                        title="Apri in Agenda"
                      >
                        📅
                      </button>
                    </div>
                  </article>
                </div>
              </div>
            );
          })}
        </section>
      </div>

      







            {/* ---- Modale gestione ---- */}
      {gestioneOpen && (
        <div className="gestione-modal-overlay">
          <div className="gestione-modal">
    <button
  className="gestione-modal__close"
  onClick={() => {
    setGestioneOpen(false);
    setAssignOpen(false); // chiudi anche la micromodale se era aperta
  }}
>
  ✕
</button>


            <h2 className="gestione-modal__title">
              Gestione appuntamento #{gestioneEventId}
            </h2>

            {gestioneLoading && (
              <div className="muted">Carico dati gestione…</div>
            )}

            {gestioneErr && !gestioneLoading && (
              <div style={{ color: "#fca5a5" }}>Errore: {gestioneErr}</div>
            )}

            {gestioneData && !gestioneLoading && !gestioneErr && (
              <div className="gm-content">
                {/* TOP: cliente + budget */}
                <div className="gm-row gm-row--top">
                  {/* CLIENTE */}
                  <div className="gm-card gm-card--cliente">
                    {(() => {
                      const ev = gestioneData.event || {};
                      const start =
                        ev.start_dt || ev.start || ev.data || ev.datetime;
                      const d = start ? new Date(start) : null;
                      const when =
                        d && !Number.isNaN(d.getTime())
                          ? `${pad2(d.getDate())}/${pad2(
                              d.getMonth() + 1
                            )} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
                          : "-";

                      const nome = ev.cliente_nome || ev.nome_cliente || "—";
                      const indirizzo =
                        ev.cliente_indirizzo || ev.indirizzo || "";
                      const cap = ev.cap || "";
                      const comune = ev.comune || "";
                      const tel =
                        ev.cliente_telefono || ev.cliente_tel || "—";

                      return (
                        <>
                          <div className="gm-cliente-line1">
                            <span className="gm-cliente-nome">{nome}</span>
                            <span className="gm-cliente-sep"> - </span>
                            <span className="gm-cliente-indirizzo">
                              {indirizzo}
                              {cap && ` • ${cap}`}
                              {comune && ` ${comune}`}
                            </span>
                          </div>
                          <div className="gm-cliente-line2">
                            <span className="gm-cliente-tel">📞 {tel}</span>
                            <span className="gm-cliente-sep"> - </span>
                            <span className="gm-cliente-when">
                              Appuntamento: {when}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* BUDGET */}
                  {/* BUDGET */}
<div className="gm-card gm-card--budget">
  {(() => {
    const ev = gestioneData.event || {};

    // Budget: stessa logica di normalizeEvent
    const budgetStd =
      ev.pv_budget ??
      ev.preventivo_budget_eur ??
      null;

    const budgetRil =
      ev.pv_budget_rilevatore_eur ??
      ev.preventivo_budget_rilevatore_eur ??
      null;

    const budgetSen =
      ev.pv_budget_senior_eur ??
      ev.preventivo_budget_senior_eur ??
      null;

    const budget =
      budgetStd != null
        ? Number(budgetStd)
        : budgetRil != null
        ? Number(budgetRil)
        : budgetSen != null
        ? Number(budgetSen)
        : null;

    // Real: come AgendaPro (prima il totale, poi eventuale target/senior)
    const realRaw =
      ev.pv_total ??
      ev.preventivo_target_eur ??
      ev.senior_importo_eur ??
      null;

    const real = realRaw != null ? Number(realRaw) : null;

    const diff = computeDiff(budget, real);

    const budgetVal =
      budget != null ? `${budget.toFixed(0)} €` : "—";

    const realVal =
      real != null ? `${real.toFixed(0)} €` : "—";

    return (
      <>
        <div className="gm-budget-compact">
          <span className="gm-budget-label">Budget</span>
          <span className="gm-budget-value">{budgetVal}</span>
          <span className="gm-budget-sep" />
          <span className="gm-budget-label">Preventivo</span>
          <span className="gm-budget-value">{realVal}</span>
        </div>
        <div className="gm-budget-diff">
          Scostamento:{" "}
          {diff ? `${diff.sign}${diff.score}%` : "n/d"}
        </div>
      </>
    );
  })()}
</div>

                </div>

                {/* MIDDLE: planner 10 giorni + mappa */}
                <div className="gm-row gm-row--middle">
                  {/* LEFT: planner 10×3 + link materiali */}
                  <div className="gm-card gm-card--planner">
                    {(() => {
                      const micro = gestioneData.microAgenda || {};
                      const microWeek = micro.week || {};



                      // Totale appuntamenti per giorno (somma su tutte le fasce)
                      const dayTotals = {};
                      Object.entries(microWeek).forEach(([dayKey, fasceDay]) => {
                        let sum = 0;
                        ["M", "P", "S"].forEach((k) => {
                          const slots = Array.isArray(fasceDay[k])
                            ? fasceDay[k]
                            : [];
                          slots.forEach((s) => {
                            const n = Number(s.num_app ?? s.occupati ?? 0);
                            if (Number.isFinite(n) && n > 0) sum += n;
                          });
                        });
                        dayTotals[dayKey] = sum;
                      });






                      const ev = gestioneData.event || {};
                      const materiale = gestioneData.materiale || {};
                      const links = gestioneData.links || {};

                      // Giorno base = giorno dell'appuntamento / microAgenda
                      const startStr =
                        micro.day ||
                        ev.start_dt ||
                        ev.start ||
                        ev.data ||
                        ev.datetime ||
                        null;

                      if (!startStr) {
                        return (
                          <>
                            <div className="gm-section-title">
                              Planner 10 giorni
                            </div>
                            <div className="muted">
                              Nessun dato disponibile per la micro-agenda.
                            </div>
                          </>
                        );
                      }

                      // Zona di lavoro (evento o selezione manuale)
                      const zonaIdEv = ev.zona_id || micro.zona_id || null;
                      const zonaIdUse =
                        agendaZonaId != null ? agendaZonaId : zonaIdEv;
                      const zonaLabel =
                        ZONE.find(
                          (z) => String(z.id) === String(zonaIdUse)
                        )?.label || "Zona sconosciuta";

                      // Finestra 10 giorni a partire dal giorno base + offset
                      const baseDayStr = startStr.substring(0, 10);
                      const baseDate = new Date(baseDayStr);
                      baseDate.setHours(0, 0, 0, 0);

                      const windowStart = new Date(baseDate);
                      windowStart.setDate(
                        baseDate.getDate() + weekOffset * PLANNER_DAYS
                      );

                      const days = [];
                      for (let i = 0; i < PLANNER_DAYS; i++) {
                        const d = new Date(windowStart);
                        d.setDate(windowStart.getDate() + i);
                        const y = d.getFullYear();
                        const m = pad2(d.getMonth() + 1);
                        const dd = pad2(d.getDate());
                        const ymd = `${y}-${m}-${dd}`;
                        const dowShort = new Intl.DateTimeFormat("it-IT", {
                          weekday: "short",
                        }).format(d);
                        days.push({
                          ymd,
                          dowShort,
                          dayNum: dd,
                          monthNum: m,
                        });
                      }

                      const rangeLabel = `dal ${days[0].dayNum}/${days[0].monthNum} al ${
                        days[days.length - 1].dayNum
                      }/${days[days.length - 1].monthNum}`;

                      const fasce = [
                        { key: "M", label: "Mattina", Icon: Sunrise },
                        { key: "P", label: "Pomeriggio", Icon: Sun },
                        { key: "S", label: "Sera", Icon: MoonStar },
                      ];

                      function getSlotInfo(dayYmd, fasciaKey) {
                        const dayData = microWeek[dayYmd] || {};
                        const slots = Array.isArray(dayData[fasciaKey])
                          ? dayData[fasciaKey]
                          : [];

                        // Se il backend non manda nulla, assumiamo 3 posti teorici liberi
                        const capacity = slots.length || 3;

                        let occupied = 0;
                        slots.forEach((s) => {
                          const n = Number(s.num_app ?? s.occupati ?? 0);
                          if (Number.isFinite(n) && n > 0) occupied += n;
                        });

                        const free = Math.max(0, capacity - occupied);
                        return { free, occupied, capacity };
                      }

                      function getSlotColorClass(free) {
                        if (free >= 2) return "planner-slot--ok"; // bordo verde
                        if (free === 1) return "planner-slot--warn"; // bordo giallo
                        return "planner-slot--full"; // bordo rosso
                      }

                      /* ===== LINK MATERIALI RILEVATORE (dentro la card) ===== */

                      const rilId =
                        ev.rilevatore_id ||
                        ev.user_id ||
                        ev.ril_id ||
                        null;

                      const rilName =
                        ev.rilevatore_nome ||
                        (rilId ? nameFromId(rilId) : "") ||
                        "Rilevatore";

                      // Audio appuntamento
                      const audioUrl =
                        ev.audio_url ||
                        links.audio_url ||
                        links.audio ||
                        materiale.audio ||
                        null;

                      // PDF interno = generato da nuovo.js (rilevatore) → lo usiamo come "FOTO"
                      const pdfInternalUrl =
                        ev.pdf_ril_url ||
                        links.pdf_ril_url ||
                        links.pdf_rilevatore ||
                        links.pdf_ril ||
                        materiale.rilevatore_pdf ||
                        null;

                      // PDF ufficiale = generato dal programma esterno
                      const pdfOfficialUrl =
                        ev.pdf_official_url ||
                        links.pdf_official_url ||
                        links.pdf_official ||
                        links.pdf_ufficiale ||
                        materiale.prev_pdf ||
                        null;

                      // R012 (può essere null se non disponibile)
                      const r012Url =
                        ev.r012_url ||
                        links.r012_url ||
                        links.r012_pdf ||
                        links.r012 ||
                        materiale.r012_url ||
                        null;

                      const DocItem = ({ label, url, icon }) => {
                        if (!url) {
                          return (
                            <div className="gm-doc-item gm-doc-item--disabled">
                              <span className="gm-doc-icon">{icon}</span>
                              <span className="gm-doc-label">
                                {label} (N.D.)
                              </span>
                            </div>
                          );
                        }
                        return (
                          <a
                            className="gm-doc-item"
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <span className="gm-doc-icon">{icon}</span>
                            <span className="gm-doc-label">{label}</span>
                          </a>
                        );
                      };

                      return (
                        <>
                          {/* header planner minimal */}

                          {/* header planner + filtri sulla stessa riga */}
<div className="gm-planner-header">
  {/* titolo zona + range */}
  <div className="gm-planner-title-main">
    <span className="gm-planner-zone">{zonaLabel}</span>
    <span className="gm-planner-range">{rangeLabel}</span>
  </div>

    {/* toggle negativi fasce/carico + settimana prossima */}
  <div className="gm-planner-filters">
    <div className="gm-planner-filters-group">
      {/* salto rapido alla settimana prossima */}
      <button
        type="button"
        className="gm-planner-chip"
        onClick={jumpToNextWeekFromToday}
        title="Vai alla settimana prossima (dal prossimo lunedì)"
      >
        1w →
      </button>

      {/* Escludi fasce: mattina/pomeriggio/sera */}
      {fasce.map(({ key, Icon }) => (
        <button
          key={key}
          type="button"
          className={
            "gm-planner-chip" + (hiddenFasce[key] ? " is-active" : "")
          }
          onClick={() => toggleFasciaVisibility(key)}
          title={
            key === "M"
              ? "Escludi mattina"
              : key === "P"
              ? "Escludi pomeriggio"
              : "Escludi sera"
          }
        >
          {Icon && <Icon size={12} strokeWidth={2} />}
        </button>
      ))}
    </div>

    <div className="gm-planner-filters-group">
      {/* dimma slot con ≥2 app */}
      <button
        type="button"
        className={
          "gm-planner-chip" + (slotFilters.dim2plus ? " is-active" : "")
        }
        onClick={() => toggleSlotFilter("dim2plus")}
        title="Dimmare slot con almeno 2 appuntamenti"
      >
        ≥2
      </button>

      {/* dimma giorni con somma app ≥4 */}
      <button
        type="button"
        className={
          "gm-planner-chip" + (slotFilters.dim4day ? " is-active" : "")
        }
        onClick={() => toggleSlotFilter("dim4day")}
        title="Dimmare giorni con almeno 4 appuntamenti totali"
      >
        Σ≥4
      </button>
    </div>
  </div>

  {/* frecce */}
  <div className="gm-week-nav">
    <button
      type="button"
      className="gm-week-btn"
      data-arrow="←"
      onClick={() => setWeekOffset((w) => w - 1)}
    />
    <button
      type="button"
      className="gm-week-btn"
      data-arrow="→"
      onClick={() => setWeekOffset((w) => w + 1)}
    />
  </div>
</div>





                          {/* Griglia 10×3 */}
                          <div className="planner-grid">
                            {/* colonna vuota sopra le etichette delle fasce */}
                            <div className="planner-col-label" />

                            {/* intestazione giorni */}
                            {days.map((day) => (
                              <div
                                key={day.ymd}
                                className={
                                  "planner-day-header" +
                                  (selectedDay === day.ymd
                                    ? " is-selected"
                                    : "")
                                }
                              >
                                <div className="planner-day-dow">
                                  {day.dowShort}
                                </div>
                                <div className="planner-day-num">
                                  {day.dayNum}
                                </div>
                              </div>
                            ))}





                            {/* righe per fascia */}
                            {fasce.map(({ key, label, Icon }) => (
                              <React.Fragment key={key}>
                                {/* etichetta fascia (colonna sinistra) */}
                                <div className="planner-col-label">
                                  <div className="planner-fascia-label">
                                    {Icon && (
                                      <Icon
                                        size={14}
                                        strokeWidth={2}
                                        className="planner-fascia-icon"
                                      />
                                    )}
                                    <span>{label}</span>
                                  </div>
                                </div>



{days.map((day) => {
  const { free, occupied } = getSlotInfo(day.ymd, key);
  const isSelected =
    selectedDay === day.ymd && selectedFascia === key;

  const colorClass = getSlotColorClass(free);

  const totalDay = dayTotals[day.ymd] || 0;

  // logica "negativa"
  const dimByFascia = hiddenFasce[key];                        // no mattina/pomeriggio/sera
  const dimBy2InSlot =
    slotFilters.dim2plus && Number(occupied) >= 2;             // slot con ≥2 app
  const dimBy4InDay =
    slotFilters.dim4day && Number(totalDay) >= 4;              // giorno con Σ app ≥4

  const isDimmed = dimByFascia || dimBy2InSlot || dimBy4InDay;

  return (
    <button
      key={`${day.ymd}-${key}`}
      type="button"
      className={
        "planner-slot " +
        colorClass +
        (isSelected ? " planner-slot--selected" : "") +
        (isDimmed ? " planner-slot--dimmed" : "")
      }
      onClick={(evt) => {
        // NB: anche se dimmed, lo slot resta selezionabile
        setSelectedDay(day.ymd);
        setSelectedFascia(key);
        if (evt.detail === 2) {
          handlePlannerContextMenu(evt, day.ymd, key);
        }
      }}
    >
      <div className="planner-slot-free">
        {free} liberi
      </div>
      <div className="planner-slot-occ">
        {occupied} app. occupati
      </div>
    </button>
  );
})}




                              </React.Fragment>
                            ))}
                          </div>

                          {/* Divider + materiale rilevatore dentro la card */}
                          <div className="gm-planner-docs-separator" />
                          <div className="gm-planner-docs">
                            <div className="gm-docs-header">
                              <div className="gm-section-title gm-section-title--small">
                                Materiale del rilevatore
                              </div>
                              <div className="gm-docs-subtitle">
                                {rilName}
                                {rilId ? ` (#${rilId})` : ""}
                              </div>
                            </div>
                            <div className="gm-docs-grid">
                              <DocItem
                                label="Audio appuntamento"
                                url={audioUrl}
                                icon="🎧"
                              />
                              <DocItem
                                label="FOTO"
                                url={pdfInternalUrl}
                                icon="🖼️"
                              />
                              <DocItem
                                label="PDF Ufficiale"
                                url={pdfOfficialUrl}
                                icon="🧾"
                              />
                              <DocItem
                                label="R012"
                                url={r012Url}
                                icon="📊"
                              />
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* RIGHT: mappa collegata al giorno/fascia selezionati */}
                  <div className="gm-card gm-card--map">
                    {(() => {
                      const ev = gestioneData.event || {};
                      const micro = gestioneData.microAgenda || {};

                      const startStr =
                        micro.day ||
                        ev.start_dt ||
                        ev.start ||
                        ev.data ||
                        ev.datetime ||
                        null;

                      if (!startStr) {
                        return (
                          <div className="gm-map-placeholder">
                            Nessuna data disponibile per la mappa.
                          </div>
                        );
                      }

                      const day = selectedDay || startStr.substring(0, 10);

                      const zonaIdEv = ev.zona_id || micro.zona_id || null;
                      const zonaIdUse =
                        agendaZonaId != null ? agendaZonaId : zonaIdEv;

                      if (!zonaIdUse) {
                        return (
                          <div className="gm-map-placeholder">
                            Nessuna zona collegata all’appuntamento.
                          </div>
                        );
                      }

                      let fasciaLabel = "";
                      if (selectedFascia === "M") fasciaLabel = "Mattina";
                      else if (selectedFascia === "P")
                        fasciaLabel = "Pomeriggio";
                      else if (selectedFascia === "S") fasciaLabel = "Sera";

                      const seniorName = selectedSeniorId
                        ? nameFromId(selectedSeniorId) ||
                          `Senior #${selectedSeniorId}`
                        : "";

                      const dataLabel = day.split("-").reverse().join("/"); // 2025-11-22 → 22/11/2025
                      const orario = ev.start_dt
                        ? String(ev.start_dt).slice(11, 16)
                        : "";

                      const params = new URLSearchParams({
                        d: day,
                        zona: String(zonaIdUse),
                      });
                      if (gestioneEventId) {
                        params.set("event_id", String(gestioneEventId));
                      }
                      if (selectedSeniorId) {
                        params.set("senior_id", String(selectedSeniorId));
                      }
                      params.set("_v", String(mapNonce));

                      const mapUrl = `/preventivi/agenda_map/map_embed_senior.php?${params.toString()}`;

                      return (
                        <>
                          <div className="gm-map-header">
                            <div className="gm-map-title">
                              {seniorName
                                ? `${seniorName} · ${dataLabel}${
                                    fasciaLabel ? " · " + fasciaLabel : ""
                                  }`
                                : `${dataLabel}${
                                    fasciaLabel ? " · " + fasciaLabel : ""
                                  }`}
                            </div>
                            <div className="gm-map-subtitle">
                              {ev.cliente_nome || ev.nome_cliente || ""}
                              {orario ? ` · ${orario}` : ""}
                              {zonaIdUse ? ` · Zona ${zonaIdUse}` : ""}
                            </div>
                          </div>
                          <iframe
                            key={mapUrl}
                            src={mapUrl}
                            className="gm-map-iframe"
                            title="Mappa appuntamenti senior"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                          />
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* BOTTOM: azioni + log compatto */}
                <div className="gm-row gm-row--bottom">
                  {/* Azioni sul cliente */}
                  <div className="gm-card gm-card--azioni">
                    <div className="gm-section-title">Azioni sul cliente</div>
                    <div className="gm-actions-bar">
                      <button
                        type="button"
                        className="gm-act-btn gm-act-btn--tel-red"
                        title="Telefonata, non risponde"
                        onClick={() => sendGestioneAction("fmt_tel_no_answer")}
                      >
                        📞
                        <span className="gm-act-label">No risposta</span>
                      </button>

                      <button
                        type="button"
                        className="gm-act-btn gm-act-btn--tel-yellow"
                        title="Da richiamare"
                        onClick={() => sendGestioneAction("fmt_tel_callback")}
                      >
                        ⏰
                        <span className="gm-act-label">Richiamare</span>
                      </button>

                      <button
                        type="button"
                        className="gm-act-btn gm-act-btn--wa"
                        title="Messaggio WhatsApp"
                        onClick={() => sendGestioneAction("fmt_whatsapp")}
                      >
                        💬
                        <span className="gm-act-label">WhatsApp</span>
                      </button>

                      <button
                        type="button"
                        className="gm-act-btn gm-act-btn--cant"
                        title="Non interessato (cantiere)"
                        onClick={() =>
                          sendGestioneAction("fmt_no_interest_site")
                        }
                      >
                        🚧
                        <span className="gm-act-label">Cantiere ✕</span>
                      </button>

                      <button
                        type="button"
                        className="gm-act-btn gm-act-btn--money"
                        title="Non interessato (budget)"
                        onClick={() =>
                          sendGestioneAction("fmt_no_interest_money")
                        }
                      >
                        💸
                        <span className="gm-act-label">Soldi ✕</span>
                      </button>

                      <button
                        type="button"
                        className="gm-act-btn gm-act-btn--ok"
                        title="Appuntamento fissato col senior"
                        onClick={() => sendGestioneAction("fmt_senior_fix")}
                      >
                        ✅
                        <span className="gm-act-label">Fix</span>
                      </button>
                    </div>
                  </div>

                  {/* Log compatto (ultime 6 azioni) */}
                  <div className="gm-card gm-card--log">
                    <div className="gm-section-title">
                      Traccia azioni (ultime 6)
                    </div>
                    {(() => {
                      const az = gestioneData.azioni || [];
                      if (!az.length) {
                        return (
                          <div className="muted">
                            Nessuna azione registrata per questo
                            appuntamento.
                          </div>
                        );
                      }
                      const last = az.slice(-6).reverse();
                      return (
                        <table className="gm-azione-table gm-azione-table--compact">
                          <thead>
                            <tr>
                              <th>Data/Ora</th>
                              <th>Azione</th>
                              <th>Oper.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {last.map((r) => (
                              <tr key={r.id}>
                                <td>{r.at}</td>
                                <td>{r.action}</td>
                                <td>
                                  {r.operator_id
                                    ? `${nameFromId(r.operator_id)} (#${
                                        r.operator_id
                                      })`
                                    : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Micromodale: fissare appuntamento col senior (click destro sul planner) */}
      {assignOpen && (
        <div
          className="gm-assign-overlay"
          onClick={() => setAssignOpen(false)}
        >
          <div
            className="gm-assign-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="gm-assign-title">
              Fissa appuntamento col senior
            </h3>

            <div className="gm-assign-row gm-assign-row--summary">
              <div>
                <div className="gm-assign-label">Giorno</div>
                <div className="gm-assign-value">
                  {assignSlot.day
                    ? assignSlot.day.split("-").reverse().join("/")
                    : "-"}
                </div>
              </div>
              <div>
                <div className="gm-assign-label">Fascia</div>
                <div className="gm-assign-value">
                  {assignSlot.fascia === "M"
                    ? "Mattina"
                    : assignSlot.fascia === "P"
                    ? "Pomeriggio"
                    : assignSlot.fascia === "S"
                    ? "Sera"
                    : "-"}
                </div>
              </div>
            </div>

            <div className="gm-assign-row">
              <label className="gm-assign-field">
                <span className="gm-assign-label">Orario</span>
                <select
                  value={assignTime}
                  onChange={(e) => setAssignTime(e.target.value)}
                >
                  {buildTimeOptionsForFascia(assignSlot.fascia || "M").map(
                    (t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    )
                  )}
                </select>
              </label>
            </div>

            <div className="gm-assign-row">
              <label className="gm-assign-field">
                <span className="gm-assign-label">Senior</span>
                <select
                  value={selectedSeniorId || ""}
                  onChange={(e) =>
                    setSelectedSeniorId(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                >
                  <option value="">— Seleziona senior —</option>
                  {seniorOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="gm-assign-row">
              <label className="gm-assign-field">
                <span className="gm-assign-label">Note (opzionali)</span>
                <textarea
                  rows={2}
                  value={assignNote}
                  onChange={(e) => setAssignNote(e.target.value)}
                />
              </label>
            </div>

            <div className="gm-assign-actions">
              <button
                type="button"
                className="btn btn--icon"
                onClick={() => setAssignOpen(false)}
              >
                Annulla
              </button>




                            <button
                type="button"
                className="btn btn--primary"

                onClick={() => {
  if (!assignSlot.day || !assignSlot.fascia) {
    alert("Seleziona un giorno/fascia.");
    return;
  }
  if (!selectedSeniorId) {
    alert("Seleziona un senior.");
    return;
  }
  if (!assignTime) {
    alert("Seleziona un orario.");
    return;
  }

  // allineo lo stato principale alla scelta della micromodale
  setSelectedDay(assignSlot.day);
  setSelectedFascia(assignSlot.fascia);

  // 🔴 NUOVO: forzo refresh mappa
  setMapNonce((n) => n + 1);

  // log + backend: fmt_senior_fix
  sendGestioneAction("fmt_senior_fix", {
    slot: {
      day: assignSlot.day,
      fascia: assignSlot.fascia,
      time: assignTime,        // HH:MM
      senior_id: selectedSeniorId,
    },
    note: assignNote || "",
  });

  setAssignOpen(false);
}}

              >
                Conferma
              </button>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
